import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role mapping based on email
  const getRoleFromEmail = (email) => {
    const emailLower = email.toLowerCase();
    if (emailLower.includes('admin')) {
      return 'admin';
    }
    return 'sales';
  };

  // Fetch or create user document in Firestore
  const fetchOrCreateUserDoc = async (firebaseUser) => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserData(data);
        return data;
      } else {
        // Create new user document
        const role = getRoleFromEmail(firebaseUser.email);
        const newUserData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          role: role,
          created_at: serverTimestamp(),
          last_login: serverTimestamp()
        };
        
        await setDoc(userRef, newUserData);
        setUserData(newUserData);
        return newUserData;
      }
    } catch (err) {
      console.error('Error fetching/creating user doc:', err);
      setError(err.message);
      return null;
    }
  };

  // Update last login
  const updateLastLogin = async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, { last_login: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.error('Error updating last login:', err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchOrCreateUserDoc(firebaseUser);
        await updateLastLogin(firebaseUser.uid);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserData(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const isAdmin = () => {
    return userData?.role === 'admin';
  };

  const value = {
    user,
    userData,
    loading,
    error,
    signIn,
    signOut,
    isAdmin,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
